// pages/Recruitment/applicantTypes.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared types + constants used across all 4 stage components
// (CandidateInformationTab, ScreenerRoundTab, InterviewRoundTab,
// OfferPlacementTab) and the main AllApplicants page that orchestrates them.
// ─────────────────────────────────────────────────────────────────────────────
export const ROUND_TYPE_ORDER = ['hr', 'tech', 'mgmt'];
export const ROUND_TYPE_LABELS = {
    hr: 'HR Round', tech: 'Technical Round', mgmt: 'Management Round',
};
// A round "passes" once Done with a positive recommendation for its type —
// mirrors backend's hasPassedRound(). Technical can't be scheduled until HR
// passes; Management can't be scheduled until Technical passes.
const POSITIVE_FEEDBACK_BY_TYPE = {
    hr: ['Recommended as P1', 'Recommended as P2'],
    tech: ['Recommended as P1', 'Recommended as P2'],
    mgmt: ['Select', 'Select with Conditions'],
};
export function hasPassedRound(rounds, roundType) {
    return (rounds || []).some((r) => r.roundType === roundType && r.schedulingStatus === 'Done' && POSITIVE_FEEDBACK_BY_TYPE[roundType].includes(r.interviewerFeedbackStatus));
}
export function roundLabel(roundType, roundNumber) {
    const base = ROUND_TYPE_LABELS[roundType];
    return roundNumber > 1 ? `${base} ${roundNumber}` : base;
}
export const HR_RECOMMENDATION_OPTIONS = ['Recommended as P1', 'Recommended as P2', 'Not Recommended', 'Candidate on Hold'];
export const TECH_RECOMMENDATION_OPTIONS = ['Recommended as P1', 'Recommended as P2', 'Not Recommended', 'Candidate on Hold'];
export const MGMT_RECOMMENDATION_OPTIONS = ['Select', 'Select with Conditions', 'Hold', 'Reject'];
export const BOND_OPTIONS = ['Not discussed', 'Willing to sign bond', 'Negotiable', 'Not willing'];
export const RATING_OPTIONS = ['Poor', 'Below Expectations', 'Meets Expectations', 'Good', 'Excellent'];
// Mirrors backend's utils/requiredCandidateDocuments.js — this app has no
// shared package between frontend and backend, so the list is duplicated;
// keep both in sync if it ever changes.
export const REQUIRED_CANDIDATE_DOCUMENTS = [
    { key: 'resume', label: 'Resume' },
    { key: 'photos', label: '2 Passport Size Colour Photographs' },
    { key: 'panOrVoterId', label: 'A Copy of PAN Card / Voter ID Card' },
    { key: 'aadhar', label: 'A Copy of Aadhar Card' },
    { key: 'bankDetails', label: 'A Copy of Bank Details' },
    { key: 'uan', label: 'A Copy of UAN Number, if Applicable' },
    { key: 'educationCertificates', label: 'A Copy of Education Certificates - 10th, 12th, Graduation, Post Graduation' },
    { key: 'previousCompanyDocs', label: "A Copy of Previous Company Details - Experience Letter, Last 3 Months' Salary Slips, if Applicable" },
];
export const API_BASE = process.env.REACT_APP_REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
export const STATUS_OPTIONS = ['New', 'Reviewed', 'Shortlisted', 'Rejected', 'Hired'];
export const STATUS_COLORS = {
    New: 'bg-blue-100   text-blue-700',
    Reviewed: 'bg-slate-100  text-slate-700',
    Shortlisted: 'bg-yellow-100 text-yellow-700',
    Rejected: 'bg-red-100    text-red-700',
    Hired: 'bg-green-100  text-green-700',
};
export const SCREENER_STATUS_OPTIONS = ['Shortlisted', 'Rejected', 'Candidate On Hold', 'Profile On Hold'];
export const SCREENER_STATUS_COLORS = {
    Shortlisted: 'bg-yellow-100 text-yellow-700',
    Rejected: 'bg-red-100    text-red-700',
    'Candidate On Hold': 'bg-orange-100 text-orange-700',
    'Profile On Hold': 'bg-purple-100 text-purple-700',
};
export const STAGE_OPTIONS = [
    'Technical Round 1', 'Technical Round 2',
    'Assessment (if any)', 'CEO Round', 'MD Round',
];
export const MODE_OPTIONS = ['Virtual', 'Face-to-Face (F2F)', 'Phone Call', 'Not Decided Yet'];
export const DECISION_OPTIONS = ['Pending', 'Offer Made', 'Rejected', 'On Hold', 'Candidate Withdrew'];
export const SCHEDULING_STATUS_OPTIONS = ['Scheduled', 'Rescheduled', 'Done', 'Cancelled'];
export const SCHEDULING_STATUS_COLORS = {
    Scheduled: 'bg-blue-100   text-blue-700',
    Rescheduled: 'bg-orange-100 text-orange-700',
    Done: 'bg-green-100  text-green-700',
    Cancelled: 'bg-red-100    text-red-700',
};
// Overall outcome of the interview stage — distinct from any single
// round's scheduling state or the interviewer's own feedback on a round.
// 'New' is the starting value and is system-controlled only (see
// maybeAdvanceInterviewStatus in routes/applicantRecords.js) — it's listed
// here so an in-progress-less candidate's dropdown still renders correctly,
// but it's always disabled as a manual selection (never selectable again
// once the record has moved past it).
export const INTERVIEW_FINAL_STATUS_OPTIONS = ['New', 'In Progress', 'Shortlisted', 'Rejected'];
export const INTERVIEW_FINAL_STATUS_COLORS = {
    New: 'bg-gray-100   text-gray-500',
    'In Progress': 'bg-blue-100   text-blue-700',
    Shortlisted: 'bg-green-100  text-green-700',
    Rejected: 'bg-red-100    text-red-700',
};
// The interviewer's own recommendation/priority ranking for a round —
// feeds into what interviewFinalStatus is allowed to be set to.
export const INTERVIEWER_FEEDBACK_STATUS_OPTIONS = [
    'Recommended as P1', 'Recommended as P2', 'Not Recommended', 'Candidate on Hold',
];
export const INTERVIEWER_FEEDBACK_STATUS_COLORS = {
    'Recommended as P1': 'bg-green-100  text-green-700',
    'Recommended as P2': 'bg-lime-100   text-lime-700',
    'Not Recommended': 'bg-red-100    text-red-700',
    'Candidate on Hold': 'bg-yellow-100 text-yellow-700',
};
// Set manually by HR, or by the candidate clicking Yes/Maybe/Can't-attend
// in the schedule/reschedule email.
export const CANDIDATE_CONFIRMATION_OPTIONS = ['Pending', 'Yes', 'Maybe', 'No'];
export const CANDIDATE_CONFIRMATION_COLORS = {
    Pending: 'bg-gray-100   text-gray-600',
    Yes: 'bg-green-100  text-green-700',
    Maybe: 'bg-orange-100 text-orange-700',
    No: 'bg-red-100    text-red-700',
};
export const DECISION_COLORS = {
    Pending: 'bg-gray-100    text-gray-600',
    'Offer Made': 'bg-green-100   text-green-700',
    Rejected: 'bg-red-100     text-red-700',
    'On Hold': 'bg-yellow-100  text-yellow-700',
    'Candidate Withdrew': 'bg-orange-100  text-orange-700',
};
export const OFFER_STATUS_COLORS = {
    'Offer Pending': 'bg-gray-100   text-gray-600',
    'Offer Released': 'bg-blue-100   text-blue-700',
    'Offer Accepted': 'bg-green-100  text-green-700',
    'Offer Declined': 'bg-red-100    text-red-700',
};
export const JOINING_STATUS_OPTIONS = ['Joining Pending', 'Joined', 'Did Not Join', 'Offer Withdrawn'];
export const JOINING_STATUS_COLORS = {
    '': 'bg-gray-100   text-gray-500',
    'Joining Pending': 'bg-blue-100   text-blue-700',
    Joined: 'bg-green-100  text-green-700',
    'Did Not Join': 'bg-red-100    text-red-700',
    'Offer Withdrawn': 'bg-orange-100 text-orange-700',
};
export const DOCS_STATUS_OPTIONS = ['Pending', 'Partially Received', 'Complete'];
export const BGV_STATUS_OPTIONS = ['Not Applicable', 'Pending', 'In Progress', 'Cleared', 'Adverse'];
export const ASSESSMENT_STATUS_COLORS = {
    '': 'bg-gray-100   text-gray-500',
    Sent: 'bg-blue-100   text-blue-700',
    Submitted: 'bg-amber-100  text-amber-700',
    Completed: 'bg-green-100  text-green-700',
    Graded: 'bg-green-100  text-green-700',
};
export const DISC_TRAIT_LABELS = {
    D: 'Dominance',
    I: 'Influence',
    S: 'Steadiness',
    C: 'Conscientiousness',
};
// Full descriptions — used where the trait needs to be shown in full
// (Candidate Information tab), not just the short name used in compact
// spots like the Attitude/DISC dropdown.
export const DISC_TRAIT_DESCRIPTIONS = {
    D: 'D (Dominance): Confident, direct, and results-driven.',
    I: 'I (Influence): Open, enthusiastic, and relationship-oriented.',
    S: 'S (Steadiness): Dependable, calm, and cooperative.',
    C: 'C (Conscientiousness): Analytical, detail-oriented, and focused on accuracy.',
};
